import {
  compileEqBlock,
  featuredInstruments,
  instrumentVoice,
  omittedInstruments,
  supportingInstruments,
} from "./eq";
import { intensityPhrase, type Dial } from "./dials";
import type { SectionCue } from "./tags";
import {
  compileVocalDetails,
  DEFAULT_VOCAL_PROFILE,
  vocalLeadLock,
  type VocalProfile,
} from "./vocals";
import { KARAOKE_CAPTION } from "./karaoke";
import { healingCaption, healingLyricHint } from "./healing";
import { DEFAULT_MIX_WEIGHTS, lanePhrase, type MixWeights } from "./mixWeights";

export type Caption = {
  global_metadata: string;
  equalizer: string;
  vocal_details: string;
  arrangement: string;
};

export type CaptionInput = {
  dials: Dial[];
  cues: SectionCue[];
  bpm: number;
  genres: string[];
  mimicName?: string;
  mimicNotes?: string;
  mimicPrompt?: string;
  mimicVocab?: string[];
  seed?: number;
  formTags?: string[];
  vocal?: VocalProfile;
  healingTones?: number[];
  mixWeights?: MixWeights;
  seedSongLabel?: string;
  karaoke?: boolean;
};

function val(dials: Dial[], id: string, fallback = 50): number {
  return dials.find((d) => d.id === id)?.value ?? fallback;
}

function bankLines(dials: Dial[], bank: Dial["bank"]): string {
  return dials
    .filter(
      (d) =>
        d.bank === bank &&
        d.id !== "hpf" &&
        d.id !== "lpf" &&
        d.id !== "mimicwt" &&
        d.id !== "styleeq" &&
        d.id !== "vocaleq",
    )
    .map((d) => intensityPhrase(d.label, d.value))
    .filter(Boolean)
    .join("; ");
}

export function mixHint(
  input: Pick<CaptionInput, "bpm" | "genres" | "healingTones" | "mixWeights" | "vocal">,
): string {
  const genreLine = input.genres.length ? input.genres.join(", ") : "contemporary";
  const w = input.mixWeights || DEFAULT_MIX_WEIGHTS;
  const heal = healingLyricHint(input.healingTones || [], input.genres, w.healing);
  const cap = healingCaption(input.healingTones || [], input.genres, w.healing);
  const voice = vocalLeadLock(input.vocal);
  return [`Production mix: BPM ${input.bpm}. Genre / style: ${genreLine}.`, voice, heal, cap]
    .filter(Boolean)
    .join(" ");
}

function featuredBlock(dials: Dial[]): string {
  const featured = featuredInstruments(dials);
  if (!featured.length) return "";
  const names = featured.map((d) => d.label).join(", ");
  const lines = featured.map((d) => {
    const level = intensityPhrase(d.label, d.value) || d.label;
    return `${level}. Play ${d.label} as ${instrumentVoice(d.label)}.`;
  });
  return (
    `Named / featured instruments (mandatory — clearly audible and characteristic; ` +
    `do not replace ${names} with a generic pop/rock bed): ${lines.join(" ")} `
  );
}

export function compileCaption(input: CaptionInput): Caption {
  const {
    dials,
    cues,
    bpm,
    genres,
    mimicName,
    mimicNotes,
    mimicPrompt,
    mimicVocab,
    seed,
    formTags,
    healingTones,
    mixWeights,
    seedSongLabel,
    karaoke,
  } = input;
  const vocal = karaoke
    ? { ...(input.vocal || DEFAULT_VOCAL_PROFILE), delivery: "instrumental" as const }
    : input.vocal;
  const w = mixWeights || DEFAULT_MIX_WEIGHTS;
  const genreLine = genres.length ? genres.join(", ") : "contemporary";
  const heal = healingCaption(healingTones || [], genres, w.healing);
  const style = bankLines(dials, "style");
  const voiceLock = vocalLeadLock(vocal);
  const mimicWt = w.mimic;
  const vocabBit =
    mimicVocab && mimicVocab.length
      ? ` Vocabulary flavor: ${mimicVocab.slice(0, 16).join(", ")}.`
      : "";
  const promptBit = mimicPrompt ? ` ${mimicPrompt.trim().slice(0, 420)}.` : "";
  const notesBit = mimicNotes ? ` ${mimicNotes.slice(0, 280)}.` : "";
  const mimicBody = mimicName
    ? `Inspired-by ${mimicName} (original performance, not a voice clone).${promptBit}${notesBit}${vocabBit} Keep Frequency / Healing / Instruments at their own weights.`
    : mimicPrompt
      ? `Inspired-by lyric style.${promptBit}${vocabBit}`
      : "No inspired-by profile loaded.";
  const mimicBit = lanePhrase("Mimic", mimicWt, mimicBody);
  const seedBit = lanePhrase(
    "Seed",
    w.seed,
    seedSongLabel
      ? `Follow seed song “${seedSongLabel}” (locked RNG ${seed ?? "n/a"}) as a reference, not a clone.`
      : typeof seed === "number"
        ? `Locked generation seed ${seed} as RNG only.`
        : "No seed locked.",
  );
  const styleBit = lanePhrase("Style", w.style, `Style: ${style || "clean modern mix"}. Genre / style: ${genreLine}.`);
  const formBit =
    formTags && formTags.length
      ? ` Form: ${formTags.map((t) => t.replace(/[\[\]]/g, "")).join(" then ")}.`
      : "";

  const global_metadata =
    `Global Metadata. ${voiceLock ? `${voiceLock} ` : ""}BPM is ${bpm}. ` +
    `LANE WEIGHTS (blend, do not overwrite): Frequency ${w.frequency}/100, Instruments ${w.instruments}/100, Vocals ${w.vocals}/100, Style ${w.style}/100, Mimic ${w.mimic}/100, Healing ${w.healing}/100, Seed ${w.seed}/100. ` +
    `${styleBit} ${mimicBit} ${seedBit}${formBit}` +
    (heal ? ` ${heal}` : "");

  const equalizer = compileEqBlock(dials, w);

  const vocalBody = compileVocalDetails(dials, cues, vocal || DEFAULT_VOCAL_PROFILE);
  const vocal_details = [voiceLock, lanePhrase("Vocals", w.vocals, vocalBody)].filter(Boolean).join(" ");

  const featured = w.instruments > 0 ? featuredBlock(dials) : "";
  const supporting = supportingInstruments(dials)
    .map((d) => intensityPhrase(d.label, d.value))
    .filter(Boolean)
    .join("; ");
  const omitted = omittedInstruments(dials)
    .map((d) => d.label)
    .join(", ");
  const formLine =
    formTags && formTags.length
      ? `Section timeline: ${formTags.map((t) => t.replace(/[\[\]]/g, "")).join(" → ")}. `
      : "";
  const karaokeBit = karaoke ? ` ${KARAOKE_CAPTION}` : "";
  const arrangement = lanePhrase(
    "Instruments",
    w.instruments,
    `Arrangement. ${formLine}${featured}` +
      `Supporting bed: ${supporting || "keep the bed thin so named instruments speak"}. ` +
      `${omitted ? `Omit: ${omitted}. ` : ""}` +
      `Groove: ${intensityPhrase("groove / drum punch", val(dials, "groove", 50))}. ` +
      `Space: ${intensityPhrase("space / reverb", val(dials, "space", 40))}.` +
      karaokeBit,
  );

  return { global_metadata, equalizer, vocal_details, arrangement };
}

export function joinCaption(c: Caption): string {
  return [c.global_metadata, c.vocal_details, c.arrangement, c.equalizer]
    .filter((s) => s && s.trim())
    .join("\n\n");
}
